import React, { useState, useEffect } from 'react';
import { productsAPI } from '../services/api';
import ImageSelector from '../components/ImageSelector';
import './ProductManagement.css';

function ProductManagement() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [brandSelection, setBrandSelection] = useState('Diamond'); // 'Diamond', 'Morton', or 'Custom'
  const [customBrand, setCustomBrand] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    product_type: 'water_softener',
    price_per_bag: '',
    weight_lbs: '',
    brand: 'Diamond',
    image_path: '',
    is_available: true,
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleBrandSelectionChange = (e) => {
    const selected = e.target.value;
    setBrandSelection(selected);

    if (selected === 'Diamond' || selected === 'Morton') {
      setFormData({ ...formData, brand: selected });
      setCustomBrand('');
    } else if (selected === 'Custom') {
      setFormData({ ...formData, brand: customBrand });
    }
  };

  const handleCustomBrandChange = (e) => {
    const value = e.target.value;
    setCustomBrand(value);
    setFormData({ ...formData, brand: value });
  };

  const handleImageChange = (imagePath) => {
    setFormData({ ...formData, image_path: imagePath });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.price_per_bag) {
      alert('Please fill in required fields');
      return;
    }

    const productData = {
      ...formData,
      price_per_bag: parseFloat(formData.price_per_bag),
      weight_lbs: formData.weight_lbs ? parseInt(formData.weight_lbs) : null,
    };

    try {
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, productData);
        alert('Product updated successfully!');
      } else {
        await productsAPI.create(productData);
        alert('Product created successfully!');
      }
      resetForm();
      loadProducts();
    } catch (error) {
      alert('Error saving product: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);

    // Determine brand selection
    const productBrand = product.brand || 'Diamond';
    if (productBrand === 'Diamond' || productBrand === 'Morton') {
      setBrandSelection(productBrand);
      setCustomBrand('');
    } else {
      setBrandSelection('Custom');
      setCustomBrand(productBrand);
    }

    setFormData({
      name: product.name || '',
      description: product.description || '',
      product_type: product.product_type || 'water_softener',
      price_per_bag: product.price_per_bag || '',
      weight_lbs: product.weight_lbs || '',
      brand: productBrand,
      image_path: product.image_path || '',
      is_available: product.is_available !== false,
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await productsAPI.delete(productId);
      alert('Product deleted successfully!');
      loadProducts();
    } catch (error) {
      alert('Error deleting product: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      product_type: 'water_softener',
      price_per_bag: '',
      weight_lbs: '',
      brand: 'Diamond',
      image_path: '',
      is_available: true,
    });
    setBrandSelection('Diamond');
    setCustomBrand('');
    setEditingProduct(null);
    setShowForm(false);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) {
      return imagePath;
    }

    // Strip the /static/product_images/ prefix if it exists
    let cleanPath = imagePath;
    if (cleanPath.startsWith('/static/product_images/')) {
      cleanPath = cleanPath.replace('/static/product_images/', '');
    }

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    return `${API_URL}/api/products/images/${cleanPath}`;
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page product-management-page">
      <div className="page-header">
        <h1 className="page-title">Product Management</h1>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              backgroundImage: 'none'
            }}
          >
            Add New Product
          </button>
        )}
      </div>

      {showForm && (
        <div className="product-form-card card">
          <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Brand</label>
                <select
                  value={brandSelection}
                  onChange={handleBrandSelectionChange}
                  className="form-input"
                >
                  <option value="Diamond">Diamond</option>
                  <option value="Morton">Morton</option>
                  <option value="Custom">Custom / Other</option>
                </select>
                {brandSelection === 'Custom' && (
                  <input
                    type="text"
                    placeholder="Enter custom brand name"
                    value={customBrand}
                    onChange={handleCustomBrandChange}
                    className="form-input"
                    style={{ marginTop: '10px' }}
                  />
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Product Type *</label>
                <select
                  name="product_type"
                  value={formData.product_type}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                >
                  <option value="snow_melt">Snow Melt Salt</option>
                  <option value="water_softener">Water Softener Salt</option>
                </select>
              </div>

              <div className="form-group">
                <label>Price per Bag ($) *</label>
                <input
                  type="number"
                  name="price_per_bag"
                  value={formData.price_per_bag}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Weight (lbs)</label>
                <input
                  type="number"
                  name="weight_lbs"
                  value={formData.weight_lbs}
                  onChange={handleInputChange}
                  min="0"
                  className="form-input"
                />
              </div>
            </div>

            <ImageSelector
              value={formData.image_path}
              onChange={handleImageChange}
            />

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_available"
                  checked={formData.is_available}
                  onChange={handleInputChange}
                />
                <span>Available for purchase</span>
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                {editingProduct ? 'Update Product' : 'Create Product'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="products-list card">
        {products.length === 0 ? (
          <p>No products found. Add your first product above!</p>
        ) : (
          <div className="products-grid">
            {products.map((product) => (
              <div key={product.id} className="product-item">
                <span className={`product-status ${product.is_available ? 'available' : 'unavailable'}`}>
                  {product.is_available ? 'Available' : 'Unavailable'}
                </span>
                {product.image_path && (
                  <div className="product-image">
                    <img src={getImageUrl(product.image_path)} alt={product.name} />
                  </div>
                )}
                <div className="product-details">
                  <h3>{product.name}</h3>
                  {product.brand && <p className="product-brand">{product.brand}</p>}
                  <p className="product-description">{product.description}</p>
                  <div className="product-meta">
                    <span className="product-price">${product.price_per_bag} / bag</span>
                    {product.weight_lbs && (
                      <span className="product-weight">{product.weight_lbs} lbs</span>
                    )}
                  </div>
                </div>
                <div className="product-actions">
                  <button
                    onClick={() => handleEdit(product)}
                    className="btn btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="btn btn-danger btn-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductManagement;
